import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ThumbsUp, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import type { CommunityPost, CommunityReply } from '@/types';

const MOCK_POSTS: Record<string, CommunityPost> = {
  '1': {
    id: '1',
    title: 'How are you handling return-to-office policies in 2026?',
    content: 'We are debating between a hybrid model and full flexibility. Would love to hear what other CPOs are doing and what retention impact you have seen.\n\nOur current setup is 3 days in office, 2 days remote. But we are losing candidates who want full remote flexibility. Engineering especially has been vocal about it.\n\nAt the same time, our CEO believes in-person collaboration is essential for culture. Looking for data or anecdotes to help navigate this.',
    author: { id: 'u1', email: 'sarah@techcorp.com', orgId: 'o1', role: 'admin', name: 'Sarah Chen' },
    topic: 'Remote Work',
    createdAt: '2026-03-20T14:30:00Z',
    replyCount: 23,
    likeCount: 47,
  },
};

const MOCK_REPLIES: CommunityReply[] = [
  {
    id: 'r1',
    content: 'We moved to a "team-first" model where each team decides their own schedule. The key was giving managers the authority to set norms. Our engagement scores actually went up 12% after the change. The data really helped convince leadership.',
    author: { id: 'u10', email: 'lisa@scaleup.io', orgId: 'o10', role: 'admin', name: 'Lisa Rodriguez' },
    createdAt: '2026-03-20T16:45:00Z',
    likeCount: 18,
  },
  {
    id: 'r2',
    content: 'We are fully remote (1,200 employees) and our attrition is 8%. We invest heavily in async documentation and quarterly offsites instead. The cost of offsites is still less than office space. Happy to share our playbook if helpful.',
    author: { id: 'u11', email: 'james@remotefirst.com', orgId: 'o11', role: 'admin', name: 'James Park' },
    createdAt: '2026-03-20T18:10:00Z',
    likeCount: 24,
  },
  {
    id: 'r3',
    content: 'We tried full flexibility and it did not work well for us. Junior employees especially struggled with onboarding and mentorship remotely. We settled on 2 days minimum in office with designated collaboration days. Attrition stabilized after about 6 months.',
    author: { id: 'u12', email: 'anna@enterprise.co', orgId: 'o12', role: 'admin', name: 'Anna Novak' },
    createdAt: '2026-03-21T09:30:00Z',
    likeCount: 15,
  },
  {
    id: 'r4',
    content: 'The data from our most recent engagement survey showed that flexibility ranked #1 for individual contributors but #3 for managers (after career growth and comp). So the answer might depend on which population you are most worried about retaining.',
    author: { id: 'u13', email: 'devon@analytics.com', orgId: 'o13', role: 'admin', name: 'Devon Williams' },
    createdAt: '2026-03-21T11:00:00Z',
    likeCount: 31,
  },
];

export default function CommunityPostPage() {
  const { postId } = useParams<{ postId: string }>();
  const [replyText, setReplyText] = useState('');
  const [replies, setReplies] = useState<CommunityReply[]>(MOCK_REPLIES);

  const post = MOCK_POSTS[postId || ''] || MOCK_POSTS['1'];

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;
    const newReply: CommunityReply = {
      id: String(Date.now()),
      content: replyText,
      author: { id: 'me', email: 'me@company.com', orgId: 'org1', role: 'admin', name: 'You' },
      createdAt: new Date().toISOString(),
      likeCount: 0,
    };
    setReplies([...replies, newReply]);
    setReplyText('');
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Link
        to="/community"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Community
      </Link>

      {/* Post */}
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
            {post.topic}
          </span>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{post.title}</h1>
        <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-xs font-medium">
              {post.author.name.charAt(0)}
            </div>
            <span className="font-medium text-gray-700">{post.author.name}</span>
          </div>
          <span>{format(new Date(post.createdAt), 'MMM d, yyyy h:mm a')}</span>
        </div>
        <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-line">
          {post.content}
        </div>
        <div className="mt-4 flex items-center gap-4 border-t border-gray-100 pt-4 text-sm text-gray-500">
          <button className="flex items-center gap-1 hover:text-primary-600 transition-colors">
            <ThumbsUp className="h-4 w-4" />
            {post.likeCount}
          </button>
          <div className="flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            {replies.length} replies
          </div>
        </div>
      </div>

      {/* Replies */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Replies</h2>
        {replies.map((reply) => (
          <div key={reply.id} className="card">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 text-xs font-medium">
                {reply.author.name.charAt(0)}
              </div>
              <span className="text-sm font-medium text-gray-700">
                {reply.author.name}
              </span>
              <span className="text-xs text-gray-400">
                {format(new Date(reply.createdAt), 'MMM d, yyyy h:mm a')}
              </span>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-line">
              {reply.content}
            </p>
            <div className="mt-3">
              <button className="flex items-center gap-1 text-sm text-gray-400 hover:text-primary-600 transition-colors">
                <ThumbsUp className="h-3.5 w-3.5" />
                {reply.likeCount}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Reply Form */}
      <div className="card">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Add a reply</h3>
        <textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          className="input-field min-h-[80px]"
          placeholder="Share your thoughts..."
        />
        <div className="mt-3 flex justify-end">
          <button
            className="btn-primary"
            onClick={handleSubmitReply}
            disabled={!replyText.trim()}
          >
            Post Reply
          </button>
        </div>
      </div>
    </div>
  );
}
